import useRequest from 'hooks/useRequest';
import { createContext, useContext } from 'react';
import { BallotsService } from 'services/BallotService';
import { CommitteeCycleModel, CommitteeCycleVotes, StandardsBallotsData } from 'store/types/StandardsBallot';

export interface StandardsBallotContextType {
  ballotData?: StandardsBallotsData;
  ballotDataError?: string;
  ballotDataLoading?: boolean;
  refetchBallotData?: () => void;
  committeeCycles?: CommitteeCycleModel[];
  votingAnnouncements?: string[];
  cycleVotes?: CommitteeCycleVotes[];
  cycleVotesError?: string;
  cycleVotesLoading?: boolean;
  refetchCycleVotes: () => void;
}

const StandardsBallotContext = createContext<StandardsBallotContextType>({} as StandardsBallotContextType);

const StandardsBallotContextProvider: React.FunctionComponent = ({ children }) => {
  const {
    data: ballotData,
    error: ballotDataError,
    loading: ballotDataLoading,
    refetch: refetchBallotData,
  } = useRequest<StandardsBallotsData>(BallotsService.getStandardsCommitteeCycles);

  const {
    data: cycleVotes,
    error: cycleVotesError,
    loading: cycleVotesLoading,
    refetch: refetchCycleVotes,
  } = useRequest<CommitteeCycleVotes[]>(BallotsService.GetCommitteeCycleVotes);

  const context = {
    ballotData,
    ballotDataError,
    ballotDataLoading,
    refetchBallotData,
    committeeCycles: ballotData?.committeeCycles,
    votingAnnouncements: ballotData?.votingAnnouncements,
    cycleVotes,
    cycleVotesError,
    cycleVotesLoading,
    refetchCycleVotes,
  };

  return <StandardsBallotContext.Provider value={context}>{children}</StandardsBallotContext.Provider>;
};

export default StandardsBallotContextProvider;

export const useStandardsBallotContext = (): StandardsBallotContextType => {
  const context = useContext(StandardsBallotContext);

  if (context === undefined) {
    throw new Error('AuthContext was used outside of its Provider');
  }

  return context;
};
